import { PhoneNumber } from "univapay-node";

const DEFAULT_COUNTRY_CODE = "81";
const REGION_CODE_LENGTH = 3;

const zoneOneAreaCodesList: Record<string, string[]> = {
    "CA": [
        "1416",
        "1204",
        "1226",
        "1236",
        "1249",
        "1250",
        "1289",
        "1306",
        "1343",
        "1354",
        "1365",
        "1368",
        "1403",
        "1418",
        "1431",
        "1437",
        "1438",
        "1450",
        "1474",
        "1506",
        "1514",
        "1519",
        "1548",
        "1579",
        "1581",
        "1587",
        "1604",
        "1613",
        "1639",
        "1647",
        "1672",
        "1705",
        "1709",
        "1742",
        "1778",
        "1782",
        "1807",
        "1819",
        "1825",
        "1867",
        "1873",
        "1902",
        "1905",
        "1263",
        "1387",
        "1428",
        "1468",
        "1584",
        "1683",
        "1753",
        "1879",
    ],
    "PR": ["1787", "1939"],
    "DO": ["1809", "1829", "1849"],
    "BB": ["1246"],
    "BM": ["1441"],
    "JM": ["1876"],
    "BS": ["1242"],
    "GU": ["1671"],
    "GD": ["1473"],
    "MP": ["1670"],
    "MS": ["1664"],
    "VG": ["1284"],
    "DM": ["1767"],
    "LC": ["1758"],
    "KN": ["1869"],
    "TT": ["1868"],
    "VC": ["1784"],
    "TC": ["1649"],
    "VI": ["1340"],
    "AS": ["1684"],
};

const isZoneOnePhoneNumber = (countryCode: string) =>
    Object.values(zoneOneAreaCodesList)
        .reduce((acc, curr) => acc.concat(curr), [])
        .includes(countryCode);

export const filterOutNonDigits = (localNumber: string) => localNumber?.replace(/\D/g, "");

/**
 * Validates a phone number object.
 *
 * @param {PhoneNumber} phoneNumber - The phone number object to validate.
 * @returns {boolean} - Returns false if the phone number is valid, otherwise true.
 *
 * Invalid if:
 * - The local number contains other characters than digits and spaces
 * - The local number (not included spaces) has a length less than 4
 */
export const validatePhoneNumber = (phoneNumber: PhoneNumber) => {
    const formattedLocalNumber = filterOutNonDigits(phoneNumber?.localNumber);

    if (!phoneNumber || !formattedLocalNumber) {
        return false;
    }

    if (!/^[0-9]+$/.test(formattedLocalNumber)) {
        return true;
    }

    return formattedLocalNumber.length < 4 || formattedLocalNumber.length > 15;
};

/**
 * Formats a phone number object.
 *
 * @param {PhoneNumber} phoneNumber - The phone number object to validate.
 * @returns {PhoneNumber} - Returns a phone number with the localNumber spaces removed.
 *
 */
export const formatPhoneNumber = (phoneNumber: PhoneNumber) => {
    const formattedLocalNumber = filterOutNonDigits(phoneNumber?.localNumber);

    const formattedNumber = {
        countryCode: phoneNumber?.countryCode || (formattedLocalNumber ? DEFAULT_COUNTRY_CODE : undefined),
        localNumber: formattedLocalNumber,
    };

    return formatPhoneNumberToAPI(formattedNumber);
};

/**
 * @param {PhoneNumber} phoneNumber - The phone number object to validate.
 * @returns {PhoneNumber} - Returns formatted zone one phone number from client to API format
 * (API expects region code to be prepended to local number)
 * Country code becomes "1" and rest is prepended to the local number
 * BE limits the country code to 3 digits, thus we need to handle it on the client side
 */
export const formatPhoneNumberToAPI = (phoneNumber: PhoneNumber) => {
    if (isZoneOnePhoneNumber(phoneNumber.countryCode)) {
        const areaCode = phoneNumber.countryCode.slice(-REGION_CODE_LENGTH);
        return {
            countryCode: "1",
            localNumber: `${areaCode}${phoneNumber.localNumber}`,
        };
    }
    return phoneNumber;
};

/**
 * @param {PhoneNumber} phoneNumber - The phone number object to validate.
 * @returns {PhoneNumber} - Returns a formatted zone one phone number to client format
 * Removes first three digits from local number and appends it to the country code
 */
export const formatPhoneNumberToClient = (phoneNumber: PhoneNumber) => {
    const areaCode = phoneNumber?.localNumber?.slice(0, REGION_CODE_LENGTH); // first 3 digits from local number
    const combinedCode = `1${areaCode}`;
    if (isZoneOnePhoneNumber(combinedCode)) {
        return {
            countryCode: combinedCode,
            localNumber: phoneNumber.localNumber.slice(REGION_CODE_LENGTH),
        };
    }

    return phoneNumber;
};
